import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, Search, FileSpreadsheet, Edit, Trash2, FolderPlus, Folder,
  Layers, CheckCircle2, Loader2, Globe, Building2, ExternalLink, Eye, Info,
  ArrowUpDown, ArrowUp, ArrowDown, Send, ArrowLeft, FileText, BookmarkCheck, CheckSquare, Square, X, Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { NotificationBell } from '@/components/NotificationBell';
import { SuperAdminService } from '@/services/superadmin.service';
import logoImg from '@/assets/Sthitih_Logo.png';
import { toast, Toaster } from 'react-hot-toast';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

export interface GlobalPsFolder {
  id: string;
  name: string;
  description?: string;
  status: 'DRAFT' | 'SAVED' | 'PUBLISHED';
  is_global?: boolean;
  assigned_clients?: string[];
  ps_count?: number;
  created_at?: string;
}

export interface GlobalProblemStatement {
  id: string;
  psId: string;
  folder_id?: string | null;
  title: string;
  category: 'Software' | 'Hardware' | 'Student Innovation' | string;
  theme: string;
  organization?: string | null;
  description: string;
  expectedSolution?: string;
  youtubeLink?: string;
  datasetFile?: string;
  status?: 'DRAFT' | 'SAVED' | 'PUBLISHED';
  created_at: string;
}

const THEME_OPTIONS = [
  'AI / Machine Learning',
  'Cyber Security & Blockchain',
  'Smart Automation & Robotics',
  'Healthcare & MedTech',
  'FinTech & AgriTech',
  'Renewable Energy & Clean Tech',
  'Smart Vehicles & EV',
  'Open Innovation'
];

// ROBUST SPREADSHEET (XLSX, XLS, CSV) BINARY PARSER
const readSpreadsheetRows = async (file: File): Promise<string[][]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) return [];

  const rawRows: any[][] = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: '',
    blankrows: false,
    raw: false,
  });

  return rawRows
    .map(row => row.map(cell => (cell != null ? String(cell).trim() : '')))
    .filter(row => row.some(cell => cell.length > 0));
};

const findHeaderRowAndMap = (rows: string[][]) => {
  for (let r = 0; r < Math.min(rows.length, 10); r++) {
    const row = rows[r];
    const map = {
      organization: -1,
      title: -1,
      category: -1,
      theme: -1,
      description: -1,
      expectedSolution: -1,
      youtubeLink: -1,
      datasetFile: -1,
    };

    row.forEach((hdr, idx) => {
      const h = hdr.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
      if (!h) return;

      if (map.organization === -1 && (
        h.includes('org') || h.includes('ministry') || h.includes('dept') || 
        h.includes('company') || h.includes('agency') || h.includes('sponsor') || h.includes('submitter')
      )) {
        map.organization = idx;
      } else if (map.title === -1 && (
        h.includes('title') || h === 'heading' || h === 'challenge' || h === 'ps heading'
      )) {
        map.title = idx;
      } else if (map.category === -1 && (
        h.includes('category') || h.includes('domain type') || h.includes('software/hardware') || h.includes('hardware/software') || h === 'type'
      )) {
        map.category = idx;
      } else if (map.theme === -1 && (
        h.includes('theme') || h.includes('technology bucket') || h.includes('tech bucket') || 
        h.includes('thrust area') || h.includes('focus area') || h.includes('sector') || h.includes('bucket') || h.includes('domain')
      )) {
        map.theme = idx;
      } else if (map.youtubeLink === -1 && (
        h.includes('youtube') || h.includes('video') || h.includes('video link') || h.includes('youtube link')
      )) {
        map.youtubeLink = idx;
      } else if (map.datasetFile === -1 && (
        h.includes('dataset') || h.includes('attachment') || h.includes('reference') || 
        h.includes('file') || h.includes('document') || (h.includes('link') && !h.includes('video'))
      )) {
        map.datasetFile = idx;
      } else if (map.description === -1 && (
        h.includes('description') || h.includes('detailed') || h.includes('background') || 
        h.includes('context') || h.includes('summary') || h.includes('details') || h === 'problem' || h.includes('problem statement')
      )) {
        map.description = idx;
      } else if (map.expectedSolution === -1 && (
        h.includes('solution') || h.includes('deliverable') || h.includes('outcome')
      )) {
        map.expectedSolution = idx;
      }
    });

    // If description was mapped but title wasn't, look for a problem statement column
    if (map.title === -1 && map.description !== -1) {
      row.forEach((hdr, idx) => {
        const h = hdr.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').trim();
        if (idx !== map.description && (h.includes('statement') || h.includes('problem'))) {
          map.title = idx;
        }
      });
    }

    const matchCount = Object.values(map).filter(v => v !== -1).length;
    if (matchCount >= 2 || map.title !== -1 || map.description !== -1) {
      return { headerRowIdx: r, colMap: map };
    }
  }

  return {
    headerRowIdx: 0,
    colMap: {
      organization: -1,
      title: -1,
      category: -1,
      theme: -1,
      description: -1,
      expectedSolution: -1,
      youtubeLink: -1,
      datasetFile: -1,
    }
  };
};

const formatCleanTitle = (rawTitle: string, desc: string, theme: string, _category?: string, _org?: string): string => {
  const t = (rawTitle || '').trim();
  const d = (desc || '').trim();

  // 1. If explicit title is given in spreadsheet, preserve it cleanly!
  if (t && t.length > 0) {
    return t;
  }

  if (!d) return theme ? `${theme} Problem Statement` : 'Problem Statement';

  // 2. Extract first sentence or line from description without truncating at hyphens
  const firstLine = d.split(/[\r\n]/)[0].trim();
  if (firstLine.length <= 130) {
    return firstLine;
  }

  const firstSentence = firstLine.split(/\.\s/)[0].trim();
  if (firstSentence.length <= 130 && firstSentence.length >= 10) {
    return firstSentence;
  }

  const trimmed = firstLine.slice(0, 120);
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace > 30) {
    return trimmed.slice(0, lastSpace) + '...';
  }
  return trimmed + '...';
};

export const normalizePsTitleAndTheme = (item: { title?: string; theme?: string; description?: string }) => {
  const rawTitle = (item?.title || '').trim();
  const rawTheme = (item?.theme || '').trim();
  const rawDesc = (item?.description || '').trim();

  let title = rawTitle;
  let theme = rawTheme;

  if (rawDesc && rawDesc !== 'No description provided.' && rawDesc !== 'Problem details provided in Excel.') {
    if (
      !rawTitle ||
      rawTitle.toLowerCase().includes(rawTheme.toLowerCase()) ||
      rawTitle.toLowerCase().endsWith('innovation') ||
      rawDesc.length > rawTitle.length
    ) {
      title = rawDesc;
    }
  }

  if (theme.endsWith(' Innovation')) {
    theme = theme.replace(/ Innovation$/, '');
  }
  if (theme.endsWith(' innovation')) {
    theme = theme.replace(/ innovation$/, '');
  }

  return { title, theme };
};

export const GlobalProblemStatementsView: React.FC = () => {
  const [folders, setFolders] = useState<GlobalPsFolder[]>([]);
  const [problemStatements, setProblemStatements] = useState<GlobalProblemStatement[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // URL Routing for repository view
  const [searchParams, setSearchParams] = useSearchParams();
  const repoParam = searchParams.get('repo');

  const selectedFolder = useMemo(() => {
    if (!repoParam) return null;
    return folders.find(f => f.id === repoParam || f.name === repoParam) || null;
  }, [folders, repoParam]);

  const handleSelectFolder = (folder: GlobalPsFolder | null) => {
    if (folder) {
      setSearchParams({ repo: folder.id });
    } else {
      setSearchParams({});
    }
  };

  // Modals
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderDesc, setNewFolderDesc] = useState('');

  // Publish Modal
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [publishingRepo, setPublishingRepo] = useState<GlobalPsFolder | null>(null);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [targetStatus, setTargetStatus] = useState<'DRAFT' | 'SAVED' | 'PUBLISHED'>('PUBLISHED');

  // Single PS Detail Modal
  const [detailPs, setDetailPs] = useState<GlobalProblemStatement | null>(null);

  // PS Form Modal
  const [isPsModalOpen, setIsPsModalOpen] = useState(false);
  const [editingPsId, setEditingPsId] = useState<string | null>(null);
  const [psForm, setPsForm] = useState({
    title: '',
    category: 'Software',
    theme: THEME_OPTIONS[0],
    organization: '',
    description: '',
    expectedSolution: '',
  });

  // Table Search, Filters, & Sorting inside a repo
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortField, setSortField] = useState<'psId' | 'title' | 'category' | 'theme' | 'organization' | 'status'>('psId');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: 'psId' | 'title' | 'category' | 'theme' | 'organization' | 'status') => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [foldersData, psData, clientsData] = await Promise.all([
        SuperAdminService.getGlobalPsFolders(),
        SuperAdminService.getGlobalProblemStatements(),
        SuperAdminService.getClients()
      ]);

      setFolders(foldersData || []);

      const seen = new Set<string>();
      const deduplicatedPs = (psData || []).filter((ps: any) => {
        const cleanPsId = (ps.psId || '').trim().toUpperCase();
        const cleanFolder = (ps.folder_id || 'root').trim();
        const cleanTitle = (ps.title || '').trim().toLowerCase();
        const key = cleanPsId ? `${cleanFolder}::psid::${cleanPsId}` : `${cleanFolder}::title::${cleanTitle}`;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setProblemStatements(deduplicatedPs);

      setClients(clientsData || []);
    } catch {
      toast.error('Failed to load repositories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Handle Excel Upload -> Automatically Creates DRAFT Repo with Excel Name & Saves rows as DRAFT
  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    try {
      setLoading(true);
      const rawRows = await readSpreadsheetRows(file);

      if (rawRows.length < 2) {
        toast.error('File contains no problem statement rows');
        setLoading(false);
        return;
      }

      const { headerRowIdx, colMap } = findHeaderRowAndMap(rawRows);

      const parsedItems: any[] = [];
      const seenSheetRows = new Set<string>();
      for (let i = headerRowIdx + 1; i < rawRows.length; i++) {
        const row = rawRows[i];
        if (!row || row.length === 0 || row.every(c => !c)) continue;

        let orgVal = colMap.organization !== -1 ? row[colMap.organization] : '';
        let titleVal = colMap.title !== -1 ? row[colMap.title] : '';
        let categoryVal = colMap.category !== -1 ? row[colMap.category] : '';
        let themeVal = colMap.theme !== -1 ? row[colMap.theme] : '';
        let descVal = colMap.description !== -1 ? row[colMap.description] : '';
        let solutionVal = colMap.expectedSolution !== -1 ? row[colMap.expectedSolution] : '';
        let ytVal = colMap.youtubeLink !== -1 ? row[colMap.youtubeLink] : '';
        let datasetVal = colMap.datasetFile !== -1 ? row[colMap.datasetFile] : '';

        // Fallback detection for missing mapped columns
        row.forEach((cellVal) => {
          if (!cellVal) return;
          if (!categoryVal && (cellVal === 'Software' || cellVal === 'Hardware' || cellVal === 'Student Innovation')) {
            categoryVal = cellVal;
          }
          if (!descVal && cellVal.length > 90) {
            descVal = cellVal;
          }
          if (!ytVal && /youtube\.com|youtu\.be/i.test(cellVal)) {
            ytVal = cellVal;
          }
          if (!datasetVal && /^https?:\/\//i.test(cellVal) && !/youtube\.com|youtu\.be/i.test(cellVal)) {
            datasetVal = cellVal;
          }
        });

        if (!descVal && titleVal) descVal = titleVal;

        if (!titleVal && !descVal) continue;

        let parsedCategory = 'Software';
        if (categoryVal.toLowerCase().includes('hardware')) parsedCategory = 'Hardware';
        else if (categoryVal.toLowerCase().includes('student') || categoryVal.toLowerCase().includes('innovation')) parsedCategory = 'Student Innovation';

        const finalTitle = formatCleanTitle(titleVal, descVal, themeVal, parsedCategory, orgVal);
        const finalExpectedSolution = solutionVal ? solutionVal.trim() : 'Refer to problem description & guidelines.';

        const rowDedupeKey = `${finalTitle.toLowerCase()}::${parsedCategory.toLowerCase()}`;
        if (seenSheetRows.has(rowDedupeKey)) {
          continue;
        }
        seenSheetRows.add(rowDedupeKey);

        parsedItems.push({
          psId: `PS-${10001 + parsedItems.length}`,
          title: finalTitle,
          category: parsedCategory,
          theme: themeVal ? themeVal : 'Open Innovation',
          organization: orgVal ? orgVal : 'Corporate / Govt Partner',
          description: descVal || titleVal || 'Problem details provided in Excel.',
          expectedSolution: finalExpectedSolution,
          youtubeLink: ytVal || '',
          datasetFile: datasetVal || '',
        });
      }

      if (parsedItems.length === 0) {
        toast.error('Could not detect problem statements in spreadsheet');
        setLoading(false);
        return;
      }

      const res = await SuperAdminService.bulkUploadExcel(fileName, parsedItems);
      if (res?.success) {
        toast.success(`Created DRAFT Repo '${res.folder.name}' with ${res.count} problem statements!`);
        await fetchInitialData();
        if (res.folder?.id) {
          setSearchParams({ repo: res.folder.id });
        }
      } else {
        toast.error('Failed to create Excel repository');
      }
    } catch (err) {
      console.error('Excel upload error:', err);
      toast.error('Failed to parse Excel file');
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    try {
      const res = await SuperAdminService.createGlobalPsFolder({
        name: newFolderName.trim(),
        description: newFolderDesc.trim(),
        status: 'DRAFT',
        is_global: true,
        assigned_clients: []
      });
      toast.success(`Created DRAFT repository '${newFolderName}'`);
      setNewFolderName('');
      setNewFolderDesc('');
      setIsFolderModalOpen(false);
      await fetchInitialData();
      if (res?.id) {
        setSearchParams({ repo: res.id });
      }
    } catch {
      toast.error('Failed to create repository');
    }
  };

  const handleOpenPublishModal = (folder: GlobalPsFolder) => {
    setPublishingRepo(folder);
    setSelectedClientIds(folder.assigned_clients || []);
    setTargetStatus(folder.status === 'PUBLISHED' ? 'PUBLISHED' : 'PUBLISHED');
    setIsPublishModalOpen(true);
  };

  const handleConfirmPublish = async () => {
    if (!publishingRepo) return;
    try {
      await SuperAdminService.publishFolder(publishingRepo.id, selectedClientIds, targetStatus);
      toast.success(`Updated access for '${publishingRepo.name}'! (${selectedClientIds.length} Clients Assigned)`);
      setIsPublishModalOpen(false);
      setPublishingRepo(null);
      fetchInitialData();
    } catch {
      toast.error('Failed to update repository publication permissions');
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!window.confirm('Delete this Problem Statement Repository and all contained problem statements?')) return;
    try {
      await SuperAdminService.deleteGlobalPsFolder(folderId);
      toast.success('Deleted repository');
      if (repoParam === folderId) setSearchParams({});
      fetchInitialData();
    } catch {
      toast.error('Failed to delete repository');
    }
  };

  const handleSavePs = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFolder) return;

    try {
      if (editingPsId) {
        toast.success('Updated problem statement');
      } else {
        await SuperAdminService.createGlobalPsFolder({
          name: psForm.title
        });
        toast.success('Saved problem statement');
      }
      setIsPsModalOpen(false);
      setEditingPsId(null);
      fetchInitialData();
    } catch {
      toast.error('Failed to save problem statement');
    }
  };

  // Filtered & Sorted PS list inside active repo (Default: Ascending PS ID)
  const currentPsList = useMemo(() => {
    if (!selectedFolder) return [];
    let list = problemStatements.filter(ps => ps.folder_id === selectedFolder.id);

    // Global frontend deduplication guard: ensure each problem statement appears exactly once
    const seenPsKeys = new Set<string>();
    list = list.filter(ps => {
      const cleanPsId = (ps.psId || '').trim().toUpperCase();
      const cleanTitle = (ps.title || '').trim().toLowerCase();
      const dedupeKey = cleanPsId ? `psid::${cleanPsId}` : `title::${cleanTitle}`;
      if (!dedupeKey || seenPsKeys.has(dedupeKey)) {
        return false;
      }
      seenPsKeys.add(dedupeKey);
      return true;
    });

    if (categoryFilter !== 'all') {
      list = list.filter(ps => ps.category.toLowerCase() === categoryFilter.toLowerCase());
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(ps =>
        ps.title.toLowerCase().includes(q) ||
        ps.psId.toLowerCase().includes(q) ||
        ps.theme.toLowerCase().includes(q) ||
        (ps.organization && ps.organization.toLowerCase().includes(q)) ||
        ps.description.toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      if (sortField === 'psId') {
        const numA = parseInt(a.psId.replace(/\D/g, ''), 10) || 0;
        const numB = parseInt(b.psId.replace(/\D/g, ''), 10) || 0;
        return sortDirection === 'asc' ? numA - numB : numB - numA;
      }

      const valA = String(a[sortField] || '').toLowerCase();
      const valB = String(b[sortField] || '').toLowerCase();
      const cmp = valA.localeCompare(valB);
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [problemStatements, selectedFolder, categoryFilter, searchTerm, sortField, sortDirection]);

  return (
    <div className="flex min-h-screen bg-pageBg text-charcoal font-sans">
      <SuperAdminSidebar />
      <Toaster position="top-right" />

      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden pl-0 pb-24 md:pl-20 md:pb-8 relative">
        <SuperAdminMobileHeader title="PS Repositories" />

        {/* Header */}
        <header className="hidden md:flex h-16 bg-cardBg border-b border-softBorder px-6 items-center justify-between sticky top-0 z-30 shadow-earth-sm">
          <div className="flex items-center gap-3">
            <img src={logoImg} alt="Logo" className="h-7 w-auto object-contain" />
            <span className="text-xs font-extrabold text-[#1F5A43] uppercase tracking-wider bg-[#DFF5E7] px-2.5 py-0.5 rounded-md border border-[#1F7A4F]/20">
              Super Admin Console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell type="superadmin" />
          </div>
        </header>

        <main className="p-4 sm:p-8 space-y-6 flex-1">
          {/* TOP BAR / BREADCRUMB */}
          <div className="bg-cardBg border border-softBorder p-5 rounded-2xl shadow-earth-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              {selectedFolder ? (
                <div className="space-y-1">
                  <button
                    onClick={() => handleSelectFolder(null)}
                    className="text-xs font-bold text-[#1F5A43] flex items-center gap-1.5 hover:underline cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to All Problem Repositories
                  </button>
                  <div className="flex items-center gap-3 pt-1">
                    <Folder className="w-5 h-5 text-amber-600" />
                    <h1 className="text-lg font-black text-charcoal">{selectedFolder.name}</h1>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase flex items-center gap-1 border ${
                      selectedFolder.status === 'PUBLISHED'
                        ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                        : selectedFolder.status === 'SAVED'
                        ? 'bg-amber-100 text-amber-900 border-amber-300'
                        : 'bg-slate-100 text-slate-700 border-slate-300'
                    }`}>
                      {selectedFolder.status === 'PUBLISHED' && <Globe className="w-3 h-3 text-emerald-700" />}
                      {selectedFolder.status === 'SAVED' && <BookmarkCheck className="w-3 h-3 text-amber-700" />}
                      {selectedFolder.status === 'DRAFT' && <FileText className="w-3 h-3 text-slate-500" />}
                      {selectedFolder.status}
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-[#1F5A43]/10 text-[#1F5A43] border border-[#1F5A43]/20 text-[10px] font-black uppercase tracking-wider">
                      GLOBAL REPOSITORIES
                    </span>
                    <h1 className="text-lg font-black text-charcoal">Problem Statement Repositories</h1>
                  </div>
                  <p className="text-xs text-mutedSubheading font-semibold mt-0.5">Create, upload, and set client access permissions for preloaded Problem Statement Repositories.</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {!selectedFolder && (
                <>
                  <button
                    onClick={() => setIsFolderModalOpen(true)}
                    className="px-4 py-2 bg-white border border-softBorder text-[#1F5A43] text-xs font-bold rounded-xl flex items-center gap-1.5 hover:bg-warmSand shadow-xs cursor-pointer"
                  >
                    <FolderPlus className="w-4 h-4 text-[#1F5A43]" />
                    <span>Create Repository</span>
                  </button>

                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="px-4 py-2 bg-[#1F5A43] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-xs hover:bg-[#164332] transition-all cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-amber-300" />
                    <span>Upload Excel (Auto-Create Repo)</span>
                  </button>
                  <input type="file" ref={fileInputRef} className="hidden" accept=".csv, .xlsx, .xls" onChange={handleExcelUpload} />
                </>
              )}

              {selectedFolder && (
                <button
                  onClick={() => handleOpenPublishModal(selectedFolder)}
                  className="px-4 py-2 bg-[#1F5A43] text-white text-xs font-black uppercase tracking-wider rounded-xl flex items-center gap-1.5 shadow-xs hover:bg-[#164332] cursor-pointer"
                >
                  <Globe className="w-4 h-4 text-amber-300" />
                  <span>Publish / Access Permissions</span>
                </button>
              )}
            </div>
          </div>

          {/* MAIN PAGE VIEW 1: REPOSITORY CARDS GRID VIEW */}
          {!selectedFolder && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-black text-mutedSubheading uppercase tracking-wider">
                  All Preloaded Repositories ({folders.length})
                </h2>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-44 bg-white border border-softBorder rounded-2xl animate-pulse p-6">
                      <div className="h-5 bg-gray-200 rounded-md w-2/3 mb-4"></div>
                      <div className="h-3 bg-gray-200 rounded-md w-full mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded-md w-4/5"></div>
                    </div>
                  ))}
                </div>
              ) : folders.length === 0 ? (
                <div className="bg-cardBg border border-softBorder rounded-2xl p-12 text-center space-y-4">
                  <Folder className="w-12 h-12 text-gray-300 mx-auto" />
                  <div>
                    <h3 className="text-sm font-bold text-charcoal">No Problem Statement Repositories Found</h3>
                    <p className="text-xs text-mutedSubheading font-medium mt-1">Upload an Excel file to automatically create a repository or click 'Create Repository'.</p>
                  </div>
                  <div className="flex justify-center gap-3 pt-2">
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="px-4 py-2 bg-[#1F5A43] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-amber-300" /> Upload Excel File
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {folders.map((folder) => {
                    const seenKeys = new Set<string>();
                    const psCount = problemStatements.filter(ps => {
                      if (ps.folder_id !== folder.id) return false;
                      const cleanKey = (ps.psId || '').trim().toUpperCase() || `title::${(ps.title || '').trim().toLowerCase()}`;
                      if (seenKeys.has(cleanKey)) return false;
                      seenKeys.add(cleanKey);
                      return true;
                    }).length;
                    const isPublished = folder.status === 'PUBLISHED';
                    const isSaved = folder.status === 'SAVED';

                    return (
                      <div
                        key={folder.id}
                        onClick={() => handleSelectFolder(folder)}
                        className="bg-cardBg border-2 border-softBorder hover:border-[#1F5A43] rounded-2xl p-6 shadow-earth-sm flex flex-col justify-between space-y-4 transition-all group cursor-pointer"
                      >
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="p-3 bg-[#DFF5E7] border border-[#1F7A4F]/20 rounded-xl text-[#1F5A43]">
                              <Folder className="w-6 h-6 text-[#1F5A43]" />
                            </div>
                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase flex items-center gap-1 border ${
                              isPublished
                                ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                                : isSaved
                                ? 'bg-amber-100 text-amber-900 border-amber-300'
                                : 'bg-slate-100 text-slate-700 border-slate-300'
                            }`}>
                              {isPublished && <Globe className="w-3 h-3 text-emerald-700" />}
                              {isSaved && <BookmarkCheck className="w-3 h-3 text-amber-700" />}
                              {!isPublished && !isSaved && <FileText className="w-3 h-3 text-slate-500" />}
                              {folder.status || 'DRAFT'}
                            </span>
                          </div>

                          <div>
                            <h3 className="text-base font-extrabold text-charcoal group-hover:text-[#1F5A43] transition-colors leading-snug">
                              {folder.name}
                            </h3>
                            <p className="text-xs text-mutedSubheading font-medium mt-1 line-clamp-2">
                              {folder.description || 'Preloaded problem statement repository.'}
                            </p>
                          </div>

                          <div className="pt-2 border-t border-softBorder flex items-center justify-between text-xs font-bold text-gray-600">
                            <span className="flex items-center gap-1 font-mono font-bold text-[#1F5A43]">
                              <Layers className="w-3.5 h-3.5 text-amber-700" /> {psCount} Problem Statements
                            </span>
                            <span className="text-[10px] font-black uppercase px-2 py-0.5 bg-gray-100 rounded border border-gray-300">
                              {folder.assigned_clients?.length ? `${folder.assigned_clients.length} Clients` : 'Unassigned'}
                            </span>
                          </div>
                        </div>

                        <div className="pt-3 border-t border-softBorder flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectFolder(folder);
                            }}
                            className="flex-1 px-3 py-2 bg-[#1F5A43] text-white text-xs font-black uppercase tracking-wider rounded-xl flex items-center justify-center gap-1.5 cursor-pointer shadow-xs hover:bg-[#164332]"
                          >
                            <Eye className="w-3.5 h-3.5" /> View PS Table
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPublishModal(folder);
                            }}
                            className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-900 text-xs font-black uppercase rounded-xl flex items-center gap-1 cursor-pointer hover:bg-amber-100"
                            title="Set Client Permissions"
                          >
                            <Globe className="w-3.5 h-3.5 text-amber-700" /> Publish
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.id);
                            }}
                            className="p-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl hover:bg-rose-100 cursor-pointer"
                            title="Delete Repository"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* MAIN PAGE VIEW 2: INSIDE REPOSITORY TABLE VIEW */}
          {selectedFolder && (
            <div className="space-y-4 bg-cardBg border border-softBorder rounded-2xl p-6 shadow-earth-sm">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-softBorder">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="Search PS ID, title, theme, organization..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="p-2.5 border border-softBorder rounded-xl text-xs bg-white text-charcoal w-72 focus:outline-hidden focus:border-[#1F5A43]"
                  />
                  <select
                    value={categoryFilter}
                    onChange={e => setCategoryFilter(e.target.value)}
                    className="p-2.5 border border-softBorder rounded-xl text-xs bg-white text-charcoal font-bold"
                  >
                    <option value="all">All Categories</option>
                    <option value="software">Software</option>
                    <option value="hardware">Hardware</option>
                    <option value="student innovation">Student Innovation</option>
                  </select>
                </div>

                <div className="text-xs font-bold text-gray-500">
                  Showing {currentPsList.length} problem statement(s) inside this repo
                </div>
              </div>

              <div className="overflow-x-auto min-h-[300px]">
                <table className="table-fixed w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-[#FAF7F2] border-b-2 border-softBorder text-[#1F5A43] font-black uppercase select-none">
                      <th
                        onClick={() => handleSort('psId')}
                        className="p-3 w-[110px] min-w-[110px] cursor-pointer hover:bg-emerald-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span>PS ID</span>
                          {sortField === 'psId' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#1F5A43]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#1F5A43]" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('title')}
                        className="p-3 w-[30%] min-w-[180px] cursor-pointer hover:bg-emerald-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span>Title</span>
                          {sortField === 'title' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#1F5A43]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#1F5A43]" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('category')}
                        className="p-3 w-[120px] min-w-[120px] cursor-pointer hover:bg-emerald-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span>Category</span>
                          {sortField === 'category' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#1F5A43]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#1F5A43]" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('theme')}
                        className="p-3 w-[22%] min-w-[140px] cursor-pointer hover:bg-emerald-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span>Theme / Track</span>
                          {sortField === 'theme' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#1F5A43]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#1F5A43]" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('organization')}
                        className="p-3 w-[22%] min-w-[140px] cursor-pointer hover:bg-emerald-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span>Organization</span>
                          {sortField === 'organization' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#1F5A43]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#1F5A43]" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('status')}
                        className="p-3 w-[90px] min-w-[90px] cursor-pointer hover:bg-emerald-50/50 transition-colors"
                      >
                        <div className="flex items-center gap-1">
                          <span>Status</span>
                          {sortField === 'status' ? (
                            sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#1F5A43]" /> : <ArrowDown className="w-3.5 h-3.5 text-[#1F5A43]" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th className="p-3 w-[100px] min-w-[100px] text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-softBorder text-charcoal font-semibold">
                    {currentPsList.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-400 italic">
                          No Problem Statements in this repository yet.
                        </td>
                      </tr>
                    ) : (
                      currentPsList.map((ps) => {
                        const { title: displayTitle, theme: displayTheme } = normalizePsTitleAndTheme(ps);
                        return (
                          <tr
                            key={ps.id}
                            className="hover:bg-warmSand/50 cursor-pointer transition-colors"
                            onClick={() => setDetailPs(ps)}
                          >
                            <td className="p-3 font-mono font-bold text-[#993300] whitespace-nowrap overflow-hidden text-ellipsis">
                              {ps.psId}
                            </td>
                            <td className="p-3 font-bold text-charcoal truncate" title={displayTitle}>
                              <span className="truncate block">{displayTitle}</span>
                            </td>
                            <td className="p-3 whitespace-nowrap">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border truncate inline-block ${
                                ps.category === 'Hardware'
                                  ? 'bg-amber-50 border-amber-200 text-amber-900'
                                  : ps.category === 'Student Innovation'
                                  ? 'bg-purple-50 border-purple-200 text-purple-900'
                                  : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                              }`}>
                                {ps.category}
                              </span>
                            </td>
                            <td className="p-3 font-medium text-gray-600 truncate" title={displayTheme}>
                              <span className="truncate block">{displayTheme}</span>
                            </td>
                          <td className="p-3 font-bold text-purple-900 truncate" title={ps.organization || 'Corporate'}>
                            <span className="truncate block">{ps.organization || 'Corporate'}</span>
                          </td>
                          <td className="p-3 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase border ${
                              ps.status === 'PUBLISHED'
                                ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                                : ps.status === 'SAVED'
                                ? 'bg-amber-100 text-amber-900 border-amber-300'
                                : 'bg-slate-100 text-slate-700 border-slate-300'
                            }`}>
                              {ps.status || 'DRAFT'}
                            </span>
                          </td>
                          <td className="p-3 text-right whitespace-nowrap">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDetailPs(ps);
                              }}
                              className="px-2.5 py-1 bg-white border border-softBorder text-[#1F5A43] text-[10px] font-bold rounded-lg cursor-pointer hover:bg-warmSand"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* CREATE FOLDER MODAL */}
      <AnimatePresence>
        {isFolderModalOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white border-2 border-softBorder rounded-3xl max-w-md w-full p-6 space-y-4 shadow-xl">
              <div className="flex items-center justify-between border-b border-softBorder pb-3">
                <h3 className="text-sm font-black text-[#1F5A43] uppercase flex items-center gap-2">
                  <FolderPlus className="w-4 h-4 text-amber-600" /> Create Problem Statement Repository
                </h3>
                <button onClick={() => setIsFolderModalOpen(false)}><X className="w-4 h-4 text-gray-500" /></button>
              </div>

              <form onSubmit={handleCreateFolder} className="space-y-4 text-xs font-bold text-charcoal">
                <div>
                  <label className="block mb-1">Repository Name:</label>
                  <input type="text" required value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="e.g. Smart India Hackathon 2026 Preloaded Repos" className="w-full p-2.5 rounded-xl border border-softBorder bg-warmSand/30" />
                </div>
                <div>
                  <label className="block mb-1">Description:</label>
                  <textarea rows={3} value={newFolderDesc} onChange={e => setNewFolderDesc(e.target.value)} placeholder="Optional description of domains & target event..." className="w-full p-2.5 rounded-xl border border-softBorder bg-warmSand/30" />
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t">
                  <button type="button" onClick={() => setIsFolderModalOpen(false)} className="px-4 py-2 bg-gray-100 rounded-xl text-xs font-bold cursor-pointer">Cancel</button>
                  <button type="submit" className="px-5 py-2 bg-[#1F5A43] text-white rounded-xl text-xs font-black uppercase cursor-pointer">Create Repo</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PUBLISH & CLIENT ACCESS ASSIGNMENT MODAL */}
      <AnimatePresence>
        {isPublishModalOpen && publishingRepo && (
          <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white border-2 border-softBorder rounded-3xl max-w-lg w-full p-6 space-y-5 shadow-2xl">
              <div className="flex items-center justify-between border-b border-softBorder pb-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-[#1F5A43]" />
                  <h3 className="text-sm font-black text-[#1F5A43] uppercase">Publish Repository to Clients</h3>
                </div>
                <button onClick={() => setIsPublishModalOpen(false)}><X className="w-4 h-4 text-gray-500" /></button>
              </div>

              <div className="space-y-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-2xl text-xs font-semibold text-amber-900 space-y-1">
                  <strong className="block font-black text-amber-950">{publishingRepo.name}</strong>
                  <p className="text-[11px] text-amber-800">Select active client organizations to grant access to this repository. Authorized client event managers will be able to view and publish these preloaded PS for their students.</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-extrabold text-charcoal uppercase block">Repository Status State:</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['DRAFT', 'SAVED', 'PUBLISHED'] as const).map(st => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => setTargetStatus(st)}
                        className={`p-2.5 rounded-xl border text-xs font-black uppercase flex items-center justify-center gap-1.5 cursor-pointer transition-all ${
                          targetStatus === st
                            ? 'bg-[#1F5A43] text-white border-[#1F5A43] shadow-xs'
                            : 'bg-white text-gray-600 border-softBorder'
                        }`}
                      >
                        {st === 'PUBLISHED' && <Globe className="w-3.5 h-3.5 text-amber-300" />}
                        {st === 'SAVED' && <BookmarkCheck className="w-3.5 h-3.5 text-amber-600" />}
                        {st === 'DRAFT' && <FileText className="w-3.5 h-3.5 text-gray-400" />}
                        <span>{st}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-extrabold text-charcoal uppercase">Select Authorized Clients ({selectedClientIds.length}):</label>
                    <button
                      type="button"
                      onClick={() => {
                        if (selectedClientIds.length === clients.length) setSelectedClientIds([]);
                        else setSelectedClientIds(clients.map(c => c.id));
                      }}
                      className="text-[10px] font-black uppercase text-[#1F5A43] hover:underline"
                    >
                      {selectedClientIds.length === clients.length ? 'Deselect All' : 'Select All Clients'}
                    </button>
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-2 border border-softBorder rounded-2xl p-3 bg-gray-50">
                    {clients.map(c => {
                      const isChecked = selectedClientIds.includes(c.id);
                      return (
                        <div
                          key={c.id}
                          onClick={() => {
                            if (isChecked) setSelectedClientIds(selectedClientIds.filter(id => id !== c.id));
                            else setSelectedClientIds([...selectedClientIds, c.id]);
                          }}
                          className={`p-2.5 rounded-xl border flex items-center justify-between gap-3 cursor-pointer transition-all ${
                            isChecked ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {isChecked ? <CheckSquare className="w-4 h-4 text-[#1F5A43]" /> : <Square className="w-4 h-4 text-gray-400" />}
                            <div>
                              <strong className="text-xs font-bold text-charcoal block">{c.name}</strong>
                              <span className="text-[10px] text-gray-500 font-mono">{c.email}</span>
                            </div>
                          </div>
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-800 rounded font-mono text-[9px] uppercase font-bold">
                            {c.plan || 'Active'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-3 border-t">
                  <button type="button" onClick={() => setIsPublishModalOpen(false)} className="px-4 py-2 bg-gray-100 rounded-xl text-xs font-bold cursor-pointer">Cancel</button>
                  <button
                    type="button"
                    onClick={handleConfirmPublish}
                    className="px-5 py-2 bg-[#1F5A43] text-white rounded-xl text-xs font-black uppercase flex items-center gap-1.5 cursor-pointer shadow-xs hover:bg-[#164332]"
                  >
                    <Globe className="w-3.5 h-3.5 text-amber-300" />
                    <span>Confirm & Set Access Permissions</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SINGLE PS DETAIL MODAL */}
      <AnimatePresence>
        {detailPs && (() => {
          const { title: detailTitle, theme: detailTheme } = normalizePsTitleAndTheme(detailPs);
          return (
            <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white border-2 border-softBorder rounded-3xl max-w-lg w-full p-6 space-y-4 shadow-2xl">
                <div className="flex items-center justify-between border-b border-softBorder pb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs font-bold text-[#993300] bg-amber-50 px-2 py-0.5 rounded border border-amber-200">{detailPs.psId}</span>
                    <h3 className="text-sm font-black text-charcoal">{detailTitle}</h3>
                  </div>
                  <button onClick={() => setDetailPs(null)}><X className="w-4 h-4 text-gray-500" /></button>
                </div>

                <div className="space-y-3 text-xs text-charcoal font-medium">
                  <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-2xl border border-gray-200">
                    <div>
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Category</span>
                      <strong className="text-xs font-bold text-[#1F5A43]">{detailPs.category}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Organization</span>
                      <strong className="text-xs font-bold text-purple-900">{detailPs.organization || 'Corporate'}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Theme / Track</span>
                      <strong className="text-xs font-bold text-charcoal">{detailTheme}</strong>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase font-bold text-gray-400 block">Status State</span>
                      <strong className="text-xs font-bold text-emerald-800">{detailPs.status || 'DRAFT'}</strong>
                    </div>
                  </div>

                <div>
                  <span className="text-[10px] uppercase font-black text-gray-500 block mb-1">Expected Solution / Outcome:</span>
                  <p className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-200 text-xs text-emerald-950 font-semibold">{detailPs.expectedSolution || 'Functional prototype deliverables.'}</p>
                </div>

                {(detailPs.youtubeLink || detailPs.datasetFile) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                    {detailPs.youtubeLink && (
                      <a
                        href={detailPs.youtubeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-xl bg-red-50 border border-red-200 text-red-900 text-xs font-bold flex items-center gap-1.5 hover:bg-red-100 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-red-600" />
                        <span className="truncate">YouTube / Video Link</span>
                      </a>
                    )}
                    {detailPs.datasetFile && (
                      <a
                        href={detailPs.datasetFile}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 text-xs font-bold flex items-center gap-1.5 hover:bg-blue-100 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
                        <span className="truncate">Dataset / Attachment Link</span>
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2 border-t">
                <button type="button" onClick={() => setDetailPs(null)} className="px-5 py-2 bg-[#1F5A43] text-white rounded-xl text-xs font-bold cursor-pointer">Close</button>
              </div>
            </motion.div>
          </div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
};

export default GlobalProblemStatementsView;
